import React, {useCallback, useContext, useState} from 'react';
import {BootstrapTable, TableHeaderColumn} from 'react-bootstrap-table';
import {useIntl} from 'react-intl';
import {useHotkeys} from "react-hotkeys-hook";
import {ModalBody, ModalFooter} from 'reactstrap';
import Button from '../button'
import ReactExport from 'react-export-excel';
import {ThemeContext} from '../../apiContexts';
import {ModalStyled} from '../../styles';
import I18nMessage from '../I18nMessage';


const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const DataTable = (props) => {
    const {
        list,
        headers,
        add,
        edit,
        attributes,
        permissions,
        remove,
        confirmText,
        keyField,
        noDataText,
        searchPlaceholder,
        search,
        loading = false,
        excelConfig
    } = props;

    const intl = useIntl();
    const [row, setSelectedRow] = useState(null);
    const [modal, toggle] = useState(false);

    useHotkeys('F1', () => add({}));
    useHotkeys('F2', () => editAction(), {}, [row]);
    useHotkeys('F3', () => permissionAction(), {}, [row]);
    useHotkeys('ctrl+r', () => removeAction(), {}, [row]);

    const onRowSelect = (row, isSelected, e) => {
        if (isSelected && row.id === 0) {
            return false;
        }
        setSelectedRow(row);
        return true;
    };

    const bgColor = (row, isSelect) => {
        if (isSelect && row.id === 0) {
            return null;
        }
        return '#2497e9';
    };

    const selectRowProp = {
        mode: 'radio',
        bgColor: bgColor,
        hideSelectColumn: true,
        clickToSelect: true,
        onSelect: onRowSelect,
    };

    const editAction = useCallback(() => {
        if (row) {
            edit(row);
        }
    }, [row]);

    const permissionAction = useCallback(() => {
        if (row) {
            permissions(row);
        }
    }, [row]);

    const removeAction = useCallback(() => {
        if (row) {
            toggle(!modal);
        }
    }, [row]);


    const deleteRow = (row) => {
        remove(row);
        toggle(!modal);
    };

    const formattConfirmText = (text) => {
        if (text && row) {
            const field = text.match(/<field>(.*?)<\/field>/g).map(function (val) {
                return val.replace(/<\/?field>/g, '');
            });

            return text.replace(field, row[field[0]]);
        }
        return '';
    };

    const theme = useContext(ThemeContext);

    const renderAlertConfirmation = () => {
        const msgRemove =
            formattConfirmText(confirmText) ||
            intl.formatMessage({id: 'sharedRemoveConfirm'});
        return (
            <ModalStyled
                theme={theme}
                isOpen={modal}
                toggle={toggle}
                className="customModal"
            >
                <ModalBody className="d-flex flex-column justify-content-center align-items-center">
                    <span className="fal fa-exclamation-triangle fa-5x text-danger mb-3"/>
                    <span dangerouslySetInnerHTML={{__html: msgRemove}}/>
                </ModalBody>
                <ModalFooter>
                    <Button
                        outline
                        color="secondary"
                        onClick={() => toggle()}
                        className=" custom-border"
                    >
                        <I18nMessage id="sharedCancel"/>
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => deleteRow(row)}
                        className="custom-shadow custom-border"
                    >
                        <I18nMessage id="sharedRemove"/>
                    </Button>
                </ModalFooter>
            </ModalStyled>
        );
    };

    const renderExcelColumn = excelConfig && excelConfig.columns.map((item, idx) => {
        return (
            <ExcelColumn key={idx} label={`${intl.formatMessage({id: item.label})}`} value={item.field}/>
        );
    });

    const createCustomToolBar = (props) => {
        return (
            <>
                <div className={'rounded-group-button'}>
                    <Button
                        className={'outline'}
                        onClick={() => add({})}
                        size={'sm'}
                        color={'theme'}>
                        <i className="fas fa-plus"/> Adicionar [F1]
                    </Button>
                    <Button
                        disabled={!row || loading}
                        className={'outline'}
                        size={'sm'}
                        color={'danger'}
                        onClick={() => removeAction()}
                    >
                        <i className="fas fa-minus"/> Remover [ctrl+r]
                    </Button>
                    <Button
                        disabled={!row || loading}
                        className={'outline'}
                        size={'sm'}
                        color={'theme'}
                        onClick={() => edit(row)}
                    >
                        <i className="fas fa-edit"/> Editar [F2]
                    </Button>
                    {/*<Button*/}
                    {/*    disabled={!row || loading}*/}
                    {/*    className={'outline'}*/}
                    {/*    size={'sm'}*/}
                    {/*    color={'theme'}*/}
                    {/*    onClick={() => attributes(row)}*/}
                    {/*>*/}
                    {/*    <i className="fas fa-list"/> Atributos*/}
                    {/*</Button>*/}
                    {permissions && (
                        <Button
                            disabled={!row || loading}
                            className={'outline'}
                            color={'theme'}
                            size={'sm'}
                            onClick={() => permissions(row)}
                        >
                            <i className="fas fa-link"/> Associar [F3]
                        </Button>
                    )}
                    {excelConfig && (
                        <ExcelFile element={
                            <Button className={'outline'}
                                    size={'sm'}
                                    color={'warning'}>
                                <i className="fas fa-download"/>
                            </Button>
                        }>
                            <ExcelSheet data={list} name={`${intl.formatMessage({id: excelConfig.fileName})}`}>
                                {renderExcelColumn}
                            </ExcelSheet>
                        </ExcelFile>
                    )}
                </div>
                <div className="search-toolbar">{props.components.searchPanel}</div>
            </>
        );
    };

    const options = {
        toolBar: createCustomToolBar,
        noDataText: noDataText || intl.formatMessage({id: 'sharedNoDataText'}),
        sizePerPageList: [{
            text: '10', value: 10,
        }, {
            text: '50', value: 50,
        }, {
            text: intl.formatMessage({id: 'sharedAll'}), value: list.length,
        }],
        sizePerPage: 10, // which size per page you want to locate as default
        prePage: intl.formatMessage({id: 'sharedPrev'}), // Previous page button text
        nextPage: intl.formatMessage({id: 'sharedNext'}), // Next page button text
        firstPage: intl.formatMessage({id: 'sharedFirst'}), // First page button text
        lastPage: intl.formatMessage({id: 'sharedLast'}), // Last page button text
    };

    const columnFormatter = (cell, row) => {
        if (typeof cell === 'boolean') {
            if (cell) {
                return intl.formatMessage({id: 'sharedYes'});
            }
            return intl.formatMessage({id: 'sharedNo'});
        }
        return cell;
    };

    const renderTableHeaderColumn = headers.map((item, idx) => {
        return (
            <TableHeaderColumn key={idx} dataField={item.field} dataSort={item.dataSort} dataFormat={columnFormatter}
                               filter={item.filter}>
                <I18nMessage id={item.title}/>
            </TableHeaderColumn>
        );
    });


    const renderTable = () => {
        const data = list.length > 0 ? list : [];

        return (
            <BootstrapTable
                options={options}
                data={data}
                search={search || true}
                keyField={keyField}
                selectRow={selectRowProp}
                searchPlaceholder={
                    searchPlaceholder || intl.formatMessage({id: 'sharedSearch'})
                }
                striped
                hover
                pagination
            >
                {renderTableHeaderColumn}
            </BootstrapTable>
        );
    };

    return (
        <>
            {renderAlertConfirmation()}
            {renderTable()}
        </>
    );
};

export default DataTable;
